// Only the fields listed in the PVR/INOX targeting document are read from the profile.
export function getAdsterIdentitySnapshot() {
  try {
    const login = JSON.parse(sessionStorage.getItem('loginInfo') || 'null');
    if (!(login?.access?.token || sessionStorage.getItem('token')) || !login?.data) return 'null';
    const { dob, gd, mob } = login.data;
    return JSON.stringify({ dob, gd, mob });
  } catch {
    return 'null';
  }
}

// Keep the login DOB current after the profile API returns updated details.
export function syncAdsterProfile(profile) {
  const login = JSON.parse(sessionStorage.getItem('loginInfo') || 'null');
  if (!login?.access?.token || !login?.data?.mob ||
      String(login.data.mob).trim() !== String(profile?.data?.mob || '').trim()) return;
  if (!Object.prototype.hasOwnProperty.call(profile.data, 'dob')) return;
  sessionStorage.setItem('loginInfo', JSON.stringify({
    ...login, data: { ...login.data, dob: profile.data.dob },
  }));
  window.dispatchEvent(new Event('adster-profile-updated'));
}

export function getAgeGroupResult(dob, today = new Date()) {
  const unavailable = (status) => ({ ageGroup: '', status });
  if (dob === null || dob === undefined || dob === '') return unavailable('missing_dob');
  if (typeof dob !== 'string') return unavailable('unsupported_dob_type');
  if (!Number.isFinite(today.getTime())) return unavailable('invalid_current_date');
  // Accept API dates and the DD-MM-YYYY login/profile input without timezone shifts.
  // Normalize separator spacing without altering the stored DOB or joining date digits.
  const dateText = dob.trim().replace(/\s*([-/.])\s*/g, '$1');
  if (!dateText) return unavailable('missing_dob');
  const iso = dateText.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/);
  const edited = dateText.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  // API responses may use a month name, e.g. 17-Sep-1996 or September 17, 1996.
  const dayFirst = dateText.match(/^(\d{1,2})[-\s]+([A-Za-z]+),?[-\s]+(\d{4})$/);
  const monthFirst = dateText.match(/^([A-Za-z]+)[-\s]+(\d{1,2}),?[-\s]+(\d{4})$/);
  const months = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
  let y, m, d;
  if (iso) [y, m, d] = iso.slice(1).map(Number);
  else if (edited) [y, m, d] = [edited[3], edited[2], edited[1]].map(Number);
  else if (dayFirst || monthFirst) {
    const monthName = (dayFirst ? dayFirst[2] : monthFirst[1]).toLowerCase();
    m = months.findIndex((month) => month === monthName || month.slice(0, 3) === monthName) + 1;
    y = Number((dayFirst || monthFirst)[3]);
    d = Number(dayFirst ? dayFirst[1] : monthFirst[2]);
  } else return unavailable('unsupported_dob_format');
  const birth = new Date(y, m - 1, d);
  if (birth.getFullYear() !== y || birth.getMonth() !== m - 1 || birth.getDate() !== d) return unavailable('invalid_calendar_date');
  const age = today.getFullYear() - y -
    (today.getMonth() < m - 1 || (today.getMonth() === m - 1 && today.getDate() < d) ? 1 : 0);
  if (age < 0) return unavailable('future_dob');
  if (age < 18) return unavailable('under_18_no_configured_group');
  if (age > 120) return unavailable('over_120');
  if (age <= 24) return { ageGroup: '18-24', status: 'calculated' };
  if (age <= 34) return { ageGroup: '25-34', status: 'calculated' };
  if (age <= 44) return { ageGroup: '35-44', status: 'calculated' };
  if (age <= 54) return { ageGroup: '45-54', status: 'calculated' };
  if (age <= 64) return { ageGroup: '55-64', status: 'calculated' };
  return { ageGroup: '65+', status: 'calculated' };
}

export function getAgeBand(dob, today = new Date()) {
  return getAgeGroupResult(dob, today).ageGroup;
}

// Reject invalid entries, rather than allowing one entry to invalidate the entire SDK call.
export function cleanTargeting(values = {}, keepBlank = false) {
  const hasControl = (text) => [...text].some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127);
  return Object.fromEntries(Object.entries(values).flatMap(([key, value]) => {
    if (!key || key.trim() !== key || /[,=]/.test(key) || hasControl(key)) return [];
    if (!['string', 'number', 'boolean'].includes(typeof value) ||
        (typeof value === 'number' && !Number.isFinite(value))) return [];
    const text = String(value).trim();
    return (text || keepBlank) && !text.includes(',') && !hasControl(text) ? [[key, text]] : [];
  }));
}

export async function getUserAdTargeting(identity = getAdsterIdentitySnapshot(), includePpid = true) {
  const profile = JSON.parse(identity);
  if (!profile) return {};
  const gender = { male: 'Male', female: 'Female', m: 'Male', f: 'Female' }[String(profile.gd).toLowerCase()];
  const ageResult = getAgeGroupResult(profile.dob);
  const targeting = { age: ageResult.ageGroup, ...cleanTargeting({ gender }) };
  console.log('[Adster age group]', ageResult);
  if (includePpid && profile.mob && window.crypto?.subtle) {
    try {
      // Hash the stored mobile number exactly; do not add an assumed country code.
      const bytes = new TextEncoder().encode(String(profile.mob).trim());
      const digest = await window.crypto.subtle.digest('SHA-512', bytes);
      targeting.ppid = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
    } catch {
      // Never fall back to sending a raw phone number if hashing is unavailable.
    }
  }
  return targeting;
}

export const serializeTargeting = (targeting) => {
  const values = cleanTargeting(targeting, true);
  return Object.entries(values).map(([key, value]) => `${key}=${value}`).join(',');
};

// The SDK supports one scalar per key (comma-separated values are invalid).
// Use the first listed value for multi-genre/language/format booking fields.
const primaryValue = (value) => Array.isArray(value) ? primaryValue(value[0]) :
  typeof value === 'string' ? value.split(',')[0].trim() : value;

export function getTicketAdTargeting(ticket, payment) {
  const movie = ticket?.orderFilmCinema;
  if (!movie) return {};
  return cleanTargeting({
    format: primaryValue(ticket?.orderTicket?.experience || movie.format),
    language: primaryValue(movie.language),
    genre: primaryValue(movie.genre),
    uniqueId: movie.filmId,
    theaters: movie.theaterId,
    payment,
  });
}
